/**
 * Nurses API Service
 * Handles all nurse management API calls
 */

import axios from '@/config/axios';

export interface ListNursesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  department?: string;
  status?: string;
  shift?: string;
  isOnDutyToday?: boolean;
  specializedAreas?: string;
  minExperienceYears?: number;
}

export interface CreateNursePayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  department: string;
  licenseNumber: string;
  certifications?: string[];
  specializedAreas?: string[];
  shift?: string;
  experienceYears?: number;
  avatarUrl?: string;
}

export interface UpdateNursePayload {
  hospitalId?: string;
  department?: string;
  licenseNumber?: string;
  certifications?: string[];
  specializedAreas?: string[];
  shift?: string;
  experienceYears?: number;
  status?: string;
  avatarUrl?: string;
}

export interface NurseProfileDto {
  id: string;
  userId: string;
  hospitalId: string | null;
  department: string;
  licenseNumber: string;
  certifications: string[];
  specializedAreas: string[];
  shift: string | null;
  experienceYears: number | null;
  avatarUrl: string | null;
  status: 'active' | 'on-leave' | 'inactive';
  isActive: boolean;
  isOnDutyToday: boolean;
}

export const nursesApi = {
  async list(params?: ListNursesParams): Promise<any> {
    const response = await axios.get('/nurses', { params });
    return response.data;
  },

  async getById(id: string): Promise<any> {
    const response = await axios.get(`/nurses/${id}`);
    return response.data;
  },

  /**
   * Returns the nurse record linked to the authenticated user. Used by the
   * `useCurrentNurse` composable to populate department / specialty in the
   * nurse-only screens — not part of the auth payload.
   */
  async getMine(): Promise<NurseProfileDto> {
    const response = await axios.get<NurseProfileDto>('/nurses/me');
    return response.data;
  },

  async create(data: CreateNursePayload): Promise<any> {
    const response = await axios.post('/nurses', data);
    return response.data;
  },

  async update(id: string, data: UpdateNursePayload): Promise<any> {
    const response = await axios.put(`/nurses/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await axios.delete(`/nurses/${id}`);
  },
};
